import type Phaser from 'phaser';
import { STAR_BITS } from '../../meta/save/schema';
import { UI } from './text';

/** 7×7 pixel star. */
const STAR_PIXELS = ['...#...', '..###..', '#######', '.#####.', '..###..', '.##.##.', '.#...#.'];
const STAR_EMPTY = 0x4a5a46;

/** Draws one star at (x, y) top-left; `scale` must be an integer for crisp pixels. */
export function drawStar(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  filled: boolean,
  scale = 1,
  color: number = UI.accent,
): void {
  g.fillStyle(filled ? color : STAR_EMPTY, 1);
  STAR_PIXELS.forEach((row, py) =>
    [...row].forEach((ch, px) => {
      if (ch === '#') g.fillRect(x + px * scale, y + py * scale, scale, scale);
    }),
  );
}

/** Three stars in a row (par, all coins, challenge), centered on x. Returns the row width. */
export function drawStarRow(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  y: number,
  bits: number,
  scale = 1,
  highlight = 0,
): number {
  const size = 7 * scale;
  const gap = 2 * scale;
  const w = 3 * size + 2 * gap;
  STAR_BITS.forEach((bit, i) => {
    const color = highlight & bit ? 0xffffff : UI.accent;
    drawStar(g, Math.round(cx - w / 2) + i * (size + gap), y, (bits & bit) !== 0, scale, color);
  });
  return w;
}

/** A 7 px check box at (x, y): ticked once the lawn is mowed. */
export function drawCheck(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  ticked: boolean,
): void {
  g.fillStyle(UI.shadow).fillRect(x, y, 8, 8);
  g.fillStyle(ticked ? UI.good : 0x3a3366).fillRect(x + 1, y + 1, 6, 6);
  if (!ticked) return;
  g.fillStyle(UI.shadow);
  for (const [cx, cy] of [
    [2, 4],
    [3, 5],
    [4, 4],
    [5, 3],
    [6, 2],
  ] as const)
    g.fillRect(x + cx, y + cy, 1, 1);
}

/**
 * The coin icon, 7×7 (the height of a line of text): a round gold coin with a raised rim (lit
 * along its top left, darker round its bottom right), its flat face set lower than the rim (the
 * rim's shadow along the face's top left) and a stamped mark in the middle.
 */
const COIN_ICON = ['..OOO..', '.OLLLO.', 'OLSSFRO', 'OLSMFDO', 'OLFFFDO', '.ORDDO.', '..OOO..'];
const COIN_COLORS: Record<string, number> = {
  O: 0x5a3a06,
  L: 0xfff3a0,
  R: 0xffd23f,
  S: 0xd99a0c,
  F: 0xf5c030,
  M: 0x9a6206,
  D: 0xb8780a,
};

/** The coin icon (7×7) at top-left (x, y); `shadow` casts its round shadow 1 px down-right. */
export function drawCoin(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  shadow?: number,
): void {
  if (shadow !== undefined) {
    COIN_ICON.forEach((row, cy) =>
      [...row].forEach((ch, cx) => {
        if (ch !== '.') g.fillStyle(shadow).fillRect(x + cx + 1, y + cy + 1, 1, 1);
      }),
    );
  }
  COIN_ICON.forEach((row, cy) =>
    [...row].forEach((ch, cx) => {
      if (ch !== '.') g.fillStyle(COIN_COLORS[ch]!).fillRect(x + cx, y + cy, 1, 1);
    }),
  );
}

/** A framed panel rectangle. */
export function drawPanel(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  g.fillStyle(UI.panel, 0.95).fillRect(x, y, w, h);
  g.lineStyle(1, UI.dim, 0.7).strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
}

// Times on screen (in their own file, so tests can reach them without Phaser).
export { formatClock, formatTime } from './time';

/** A small padlock (9 px): something still to come. */
export function drawPadlock(g: Phaser.GameObjects.Graphics, x: number, y: number): void {
  g.fillStyle(0x6f68a0).fillRect(x + 2, y + 4, 5, 5);
  g.fillStyle(0x6f68a0)
    .fillRect(x + 3, y + 1, 1, 3)
    .fillRect(x + 5, y + 1, 1, 3)
    .fillRect(x + 3, y + 1, 3, 1);
}
